/* OnSumn — FlowsTransitMap: the Bay, drawn from real GTFS track geometry.
 *
 * Phase 1 T9 (spec §2). The Catmull-Rom fake is gone: every line is drawn from
 * data/lines.json (100%-accurate 511 shapes). Color on the map encodes ONE
 * thing — reachability from your home station (the ripple). The base network
 * goes quiet; glow is spent only on home + the events you can actually reach.
 * "Quieter map, louder signal."
 *
 * Channeling Reya (component arch) + Dex (marker motion) + Nia (glass/lens) +
 * Juno (marker interactions + a11y).
 */

const { useState, useEffect, useRef, useCallback } = React;

// ---------------------------------------------------------------------------
// Lens palette — spec §0.5, defined once per file, never re-derived. Both
// themes are intentional (feedback_dark_light_mode). Every ramp hex was
// contrast-measured against the surface it sits on (see §6.1 C-table).
// ---------------------------------------------------------------------------
const LENS = {
  dark: {
    page: '#0C0F0F', panel: '#1A1C1C', hairline: 'rgba(255,255,255,0.08)',
    // reach ramp (warm → cool), bands at 20/40/60
    reach1: '#FF6E32', reach2: '#FFB778', reach3: '#8FBF9F', reach4: '#A3BDCC',
    // "you can get there, not back"
    dimText: '#8A8578', dimFill: '#77716A',
    // home marker accents
    homeAccent: '#FF6E32', homeGlow: 'rgba(255,110,50,0.75)', homeInk: '#10110F',
    // P1 self-basemap ground (2026-08-26): water == page (the Bay floats in
    // the page ground; --surface-container-lowest), land ~4.5 lightness points
    // up — a subtle luminance delta, never a competing surface. Hairline
    // coastline separates them. Ground only — the transit lines/markers keep
    // exactly their pre-P1 prominence.
    groundWater: '#0C0F0F', groundLand: '#161A19', coast: 'rgba(255,255,255,0.10)',
  },
  light: {
    page: '#F5F2EC', panel: '#FBF9F4', hairline: 'rgba(26,23,20,0.12)',
    reach1: '#BF3B00', reach2: '#8E4E00', reach3: '#395542', reach4: '#3F5E70',
    dimText: '#6B5B4F', dimFill: '#8A8578',
    homeAccent: '#BF3B00', homeGlow: 'rgba(191,59,0,0.45)', homeInk: '#ffffff',
    // Same polarity as dark (water always the darker of the pair): land ==
    // page, water ~4.7 lightness points down with a fog-blue cool cast.
    groundWater: '#E2E7E4', groundLand: '#F5F2EC', coast: 'rgba(26,23,20,0.18)',
  },
};

// System line ink (§0.5). Lines only — toggles/dots keep the canonical
// SYSTEM_INFO hex. On light tiles the canonical BART/Muni/VTA hexes fail the
// 3:1 non-text floor, so the renderer maps system → this deeper ink. Caltrain
// gets brand red (NOT the feed grey `#dcddde`, which reads "disabled line").
const LINE_INK = {
  dark:  { bart: '#16A6E6', caltrain: '#E31837', muni: '#6CA085', vta: '#C0508A' },
  light: { bart: '#0C6D9C', caltrain: '#C21530', muni: '#3F6B55', vta: '#A63C74' },
};

// Toggle / draw order (§2.2 — 4 toggles). Also the marker+line system list.
const SYSTEMS = ['bart', 'caltrain', 'muni', 'vta'];

// Core weight per system (§2.2): BART/Caltrain 2.5, Muni/VTA 2.
const CORE_WEIGHT = { bart: 2.5, caltrain: 2.5, muni: 2, vta: 2 };

// Below this zoom, cull marker labels to the nearest-N events by time-from-home
// (§2.5 declutter). Any marker that IS rendered still carries its number.
const LABEL_CULL_ZOOM = 11;
const LABEL_CULL_KEEP = 30;

// Home-anchored fly-to zoom (§2.7).
const HOME_ZOOM = 12;

// "Deep night" reset: before 5am we're still in the prior service day's tail,
// so last-departure clocks (24h+ raws) compare in that frame (§ dim rule).
const NIGHT_RESET_MIN = 300;

// Band → stagger order for the entrance choreography (§2.7, reach1 first).
const BAND_ORDER = { reach1: 0, reach2: 1, reach3: 2, reach4: 3 };

// ---------------------------------------------------------------------------
// P1 (2026-08-26): basemap mode. CARTO began watermarking keyless tile
// traffic ("API KEY REQUIRED" diagonals, verified on prod), so the default
// basemap is now SELF-CONTAINED: committed Natural Earth land polygons
// (data/bay-context.geojson, public domain — see its meta + the generator
// scrapers/assets/make-bay-context.js) drawn over a water-colored ground.
// The real BART map has no streets; a transit diagram with a coastline is
// the whole idea. 'carto' keeps the tile path intact so a future keyed
// CARTO account can re-enable tiles without surgery:
//   window.FLOWS_BASEMAP = 'carto'   (set before mount; anything else → self)
// ---------------------------------------------------------------------------
const FLOWS_BASEMAP_DEFAULT = 'self';
function flowsBasemapMode() {
  const v = typeof window !== 'undefined' ? window.FLOWS_BASEMAP : null;
  return v === 'carto' || v === 'self' ? v : FLOWS_BASEMAP_DEFAULT;
}

// Dedicated pane for the ground: above tilePane (200), below the vector
// overlayPane (400) — transit lines can never sink beneath the land fill.
const FLOWS_GROUND_PANE = 'flows-ground';

// [ [S,W], [N,E] ] — mirrors data/bay-context.geojson meta.bbox (the artifact
// is the source of truth; tests/flows-basemap-self.test.js pins this mirror
// against it). In 'self' mode the map clamps here so the frame never pans
// into void beyond the committed geography.
const CONTEXT_BBOX_LATLNG = [[36.75, -123.15], [38.45, -121.35]];

// ---------------------------------------------------------------------------
// Pure helpers (exposed on window.FlowsMapInternals for unit tests — no side
// effects; the Leaflet DOM around them is Jace's manual-smoke surface).
// ---------------------------------------------------------------------------

// GeoJSON coordinates are [lng, lat]; Leaflet wants [lat, lng]. Returns an
// array of parts (one for a LineString, N for a MultiLineString) so the caller
// draws each part as its own polyline.
function geojsonToLatLngs(geometry) {
  if (!geometry || !geometry.coordinates) return [];
  const swap = (c) => [c[1], c[0]];
  if (geometry.type === 'MultiLineString') {
    return geometry.coordinates.map((part) => part.map(swap));
  }
  if (geometry.type === 'LineString') {
    return [geometry.coordinates.map(swap)];
  }
  return [];
}

// Reach band → hex (theme-tuned). Rounds first (via window.bandForMinutes) so
// the color always matches the rendered "~X min" number. null for non-finite.
function reachColor(minutes, darkMode) {
  const band = (typeof window !== 'undefined' && window.bandForMinutes)
    ? window.bandForMinutes(minutes) : null;
  if (!band) return null;
  return (darkMode ? LENS.dark : LENS.light)[band] || null;
}

function systemLineInk(system, darkMode) {
  return (darkMode ? LINE_INK.dark : LINE_INK.light)[system] || '#888';
}

// The mandatory non-color cue (§2.5): the "~X MIN" figure as text. Compact caps
// for map labels, routed through the single time formatter (§3, kills the float
// bug). No headway is passed — ranges (§3.3) need the first-boarding-line
// headway, which lives on the graph meta and is the feed/detail's job (T10);
// the base minute value is identical across surfaces regardless (AC-3.3).
function minLabelText(minutes, gone) {
  const base = (typeof window !== 'undefined' && window.formatReachTime)
    ? window.formatReachTime(minutes, undefined, { compact: true })
    : '~' + Math.round(minutes) + ' MIN';
  return gone ? base + ' · LAST TRAIN GONE' : base;
}

// "HH:MM[:SS]" → minutes since midnight. 24h+ raws (e.g. "26:10:00" = 2:10 AM
// next day) are NOT folded, so a post-midnight last train compares correctly
// against an evening clock.
function rawToMin(raw) {
  if (typeof raw !== 'string') return null;
  const m = raw.match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

// Dim predicate (§2.4): "you can get there, not back." Station/system-level —
// is the last train on this station's system already gone at `nowMin` today?
// Per-event, per-return-line precision (§5.3 tiers) rides with the feed/detail
// chips (T10), which own the event start-minutes the map doesn't carry.
// Fail-open: unknown data never dims a marker (never falsely strand a rider).
function lastTrainGone(system, nowMin, dayType, lastTrainData) {
  if (!lastTrainData || !lastTrainData.meta) return false;
  const per = lastTrainData.meta.perSystemLastService;
  if (!per || !per[system]) return false;           // unknown system → fail-open
  const rec = per[system][dayType];
  if (!rec || !rec.raw) return true;                 // system runs, but not today
  const L = rawToMin(rec.raw);
  if (L == null) return false;
  const eff = nowMin < NIGHT_RESET_MIN ? nowMin + 1440 : nowMin;
  return eff >= L;
}

// hex (#rrggbb) → rgba(...) at alpha; passthrough for anything already rgba.
function hexA(hex, alpha) {
  if (typeof hex !== 'string' || hex[0] !== '#' || hex.length !== 7) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')';
}

// Current Pacific minutes-of-day + dayType. Intl only — never getHours() on a
// parsed ISO (feedback_pacific_time_in_scrapers).
function pacificNow() {
  const band = (typeof window !== 'undefined' && window.currentBand)
    ? window.currentBand() : { dayType: 'weekday' };
  let mins = 0;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles', hour12: false, hour: '2-digit', minute: '2-digit',
    }).formatToParts(new Date());
    let h = 0, m = 0;
    for (const p of parts) {
      if (p.type === 'hour') h = parseInt(p.value, 10) % 24;
      else if (p.type === 'minute') m = parseInt(p.value, 10);
    }
    mins = h * 60 + m;
  } catch (e) { mins = 0; }
  return { nowMin: mins, dayType: band.dayType };
}

// §6.3 parity (phase-review C1): markers render ONLY for stations bearing a
// Pacific-TODAY event — the exact predicate the feed's population rule uses
// (window.FlowsFeedFormat.isEventToday, load-ordered before this module), so
// every marker's event exists as a feed row BY CONSTRUCTION. eventMap itself
// stays all-dates (FlowsDetail's station event list is legitimately so).
// Fail-open if the lib is somehow absent (page-never-dies; flows.html's
// load-order contract makes that unreachable in production).
function stationHasTonightEvent(evts, today) {
  const FF = typeof window !== 'undefined' ? window.FlowsFeedFormat : null;
  if (!FF || !FF.isEventToday) return true;
  for (let i = 0; i < evts.length; i++) {
    if (FF.isEventToday(evts[i], today)) return true;
  }
  return false;
}

// Candidate stations for reach markers: tonight-event-bearing, in a visible
// system, not home, reachable. Pure — drawLayers consumes it; the §6.3
// count-parity test asserts it against the feed on a mixed fixture.
// B10-2: ONE marker per PHYSICAL station. The list is deduped to one entry
// per (canonicalId ?? id) and the CANONICAL object (id === canonicalId) is
// rendered as the representative, so the shown name is the canonical's
// (4th & Brannan surfaces the vernacular, not "Fourth Street…"). Deduping the
// LIST is load-bearing — merely reading the eventMap by canonicalId would put
// TWO markers on one bucket. The home check and the eventMap/distances reads
// key canonically too, so a legacy raw-twin home still hides its whole
// physical station. A canonicalId-absent snapshot (deploy skew) degrades to
// raw ids — the pre-B10 behavior, never a mass collapse.
function tonightCandidateStations(stations, eventMap, home, distances, visibleSystems, today) {
  const list = stations || [];
  const homeKey = home ? (home.canonicalId ?? home.id) : null;
  const seen = new Set();
  const out = [];
  for (const s of list) {
    if (!s) continue;
    const key = s.canonicalId ?? s.id;
    if (seen.has(key)) continue;
    seen.add(key); // one evaluation per physical station, whichever twin appears first
    const rep = s.id === key ? s : (list.find((c) => c && c.id === key) || s);
    if (!visibleSystems || !visibleSystems.has(rep.system)) continue;
    if (homeKey && key === homeKey) continue;
    const evts = eventMap ? eventMap.get(key) : null;
    if (!(evts && evts.length > 0)) continue;
    if (!stationHasTonightEvent(evts, today)) continue;
    const d = distances ? distances.get(key) : null;
    if (d && isFinite(d.time)) out.push(rep);
  }
  return out;
}

if (typeof window !== 'undefined') {
  window.FlowsMapInternals = {
    SYSTEMS, LENS, LINE_INK,
    geojsonToLatLngs, reachColor, systemLineInk, minLabelText, lastTrainGone,
    stationHasTonightEvent, tonightCandidateStations,
    flowsBasemapMode, FLOWS_GROUND_PANE, CONTEXT_BBOX_LATLNG,
  };
}

// ---------------------------------------------------------------------------
// Inject pin + tooltip styles (once per theme/motion state)
// ---------------------------------------------------------------------------
const FLOWS_MAP_STYLE_ID = 'flows-transit-map-styles';

function ensureMapStyles(darkMode) {
  const existing = document.getElementById(FLOWS_MAP_STYLE_ID);
  if (existing) existing.remove();

  const L = darkMode ? LENS.dark : LENS.light;
  const panelSolid = darkMode ? 'rgba(26,28,28,0.97)' : 'rgba(251,249,244,0.97)';
  const homeCoreBg = darkMode ? '#ffffff' : '#FF4F00';
  const homeCoreOutline = darkMode ? 'none' : '2px solid #FBF9F4';
  // Spec §2.3 ring + glow, from the LENS tokens (define once, never re-derive):
  // dark 0 0 0 3px #FF6E32 + 0 0 16px rgba(255,110,50,0.75);
  // light 0 0 0 3px #BF3B00 + 0 0 12px rgba(191,59,0,0.45).
  const homeCoreShadow =
    '0 0 0 3px ' + L.homeAccent + ', 0 0 ' + (darkMode ? '16px' : '12px') + ' ' + L.homeGlow;

  const style = document.createElement('style');
  style.id = FLOWS_MAP_STYLE_ID;
  style.textContent = `
    /* Soft outer halo for transit lines — a blurred underlay turns a flat
       stroke into a glowing artery. Degrades gracefully if the SVG filter is
       skipped. Lines are STATIC now (breathe removed, §2.2). */
    .flows-line-glow { filter: blur(3.5px); }

    /* Reach dot — 10px, fill + ring set inline per marker (§2.4). */
    .flows-reach-dot { display: block; border-radius: 50%; }

    /* Home "you are here" marker (§2.3). Core = static ring even under reduced
       motion; the two expanding rings are only emitted when motion is allowed. */
    .flows-home-core {
      position: absolute; top: 50%; left: 50%;
      width: 16px; height: 16px; margin: -8px 0 0 -8px;
      border-radius: 50%; background: ${homeCoreBg};
      outline: ${homeCoreOutline}; outline-offset: -2px;
      box-shadow: ${homeCoreShadow};
    }
    .flows-home-ring {
      position: absolute; top: 50%; left: 50%;
      width: 34px; height: 34px; margin: -17px 0 0 -17px;
      border-radius: 50%; border: 2px solid ${L.homeAccent};
      opacity: 0.6;
    }
    @media (prefers-reduced-motion: no-preference) {
      .flows-home-ring { animation: onsumnPulse 2.4s ease-out infinite; }
      .flows-home-ring-2 { animation-delay: 1.2s; }
    }
    @keyframes onsumnPulse {
      0%   { transform: scale(0.5); opacity: 0.7; }
      100% { transform: scale(2.4); opacity: 0; }
    }

    /* Permanent "~X MIN" label (§2.4) — panel-solid over the map, no tip. */
    .flows-min-label {
      background: ${panelSolid} !important;
      border: 1px solid ${L.hairline} !important;
      border-radius: 6px !important;
      padding: 2px 7px !important;
      font-family: var(--font-body) !important;
      font-size: 10px !important; font-weight: 700 !important;
      letter-spacing: 0.06em !important;
      box-shadow: 0 2px 8px rgba(0,0,0,${darkMode ? '0.4' : '0.14'}) !important;
      white-space: nowrap !important;
    }
    .flows-min-label .leaflet-tooltip-tip { display: none !important; }

    /* Home label "YOU — {NAME}" (§2.3) — accent bg, ink text, caps. */
    .flows-home-label {
      background: ${L.homeAccent} !important;
      color: ${L.homeInk} !important;
      border: none !important;
      border-radius: 6px !important;
      padding: 3px 8px !important;
      font-family: var(--font-display) !important;
      font-size: 10px !important; font-weight: 800 !important;
      letter-spacing: 0.06em !important; text-transform: uppercase !important;
      box-shadow: 0 2px 10px rgba(0,0,0,${darkMode ? '0.45' : '0.2'}) !important;
      white-space: nowrap !important;
    }
    .flows-home-label .leaflet-tooltip-tip { display: none !important; }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// TransitLayerToggle — 44px min (§6.6), aria-pressed, label adjacent to the
// color dot (§6.8 non-color cue).
// ---------------------------------------------------------------------------
function TransitLayerToggle({ system, label, color, active, onToggle, lens, darkMode }) {
  const [hov, setHov] = useState(false);
  return (
    React.createElement('button', {
      onClick: () => onToggle(system),
      onMouseEnter: () => setHov(true),
      onMouseLeave: () => setHov(false),
      'aria-pressed': active,
      className: 'focus-double-ring',
      style: {
        display: 'inline-flex', alignItems: 'center', gap: 7,
        minHeight: 44, padding: '8px 14px',
        fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        // Active label uses ink (both themes readable); inactive is muted.
        // Fixes the old white-on-light-wash toggle contrast miss. Hover lifts
        // an inactive label to ink — the old 0.75 opacity dim fell to 3.4:1.
        color: active || hov ? lens.ink : lens.muted,
        background: active
          ? (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(26,23,20,0.05)')
          : 'transparent',
        border: '1px solid ' + (active ? color : 'transparent'),
        borderRadius: 100,
        cursor: 'pointer',
        transition: 'color 0.2s, background 0.2s, border-color 0.2s',
      },
    },
      React.createElement('span', {
        'aria-hidden': 'true',
        style: {
          width: 8, height: 8, borderRadius: '50%', background: color,
          opacity: active ? 1 : 0.35, flexShrink: 0, transition: 'opacity 0.2s',
        },
      }),
      label
    )
  );
}

// ---------------------------------------------------------------------------
// FlowsTransitMap
// ---------------------------------------------------------------------------
function FlowsTransitMap({
  stations,
  eventMap,
  activeStation,
  homeStation,
  onSelectStation,
  visibleSystems,
  onToggleSystem,
  darkMode,
  height,
  distances,
  lines,
  lastTrain,
  landmask, // eslint-disable-line no-unused-vars — water-gating is Sable T11 (AC-2.7); UI trusts the data.
  baseGeo,  // P1: data/bay-context.geojson (self-basemap land polygons); null degrades to bare water ground.
}) {
  // Bare call — flows.html loads lib/use-reduced-motion.js (plain defer script)
  // before any module executes, and the hook itself short-circuits to false if
  // React is somehow absent.
  const reducedMotion = window.useReducedMotion();

  const containerRef = useRef(null);
  const mapRef = useRef(null);

  const lineLayersRef = useRef({});
  const markerLayersRef = useRef({});
  const lastHomeIdRef = useRef(null);   // flyTo framing — once per home change
  const lastStaggerHomeIdRef = useRef(null); // B11 — marker stagger, keyed to the first marker-producing draw
  const belowCullRef = useRef(false);   // label-cull zoom band (§2.5)
  const homeRef = useRef(null);         // latest home, read by the init effect
  const [mapReady, setMapReady] = useState(0);
  const [zoomTick, setZoomTick] = useState(0); // bumps only when crossing the cull zoom

  const home = homeStation || activeStation || null;
  homeRef.current = home;

  // FlowsApp passes an inline arrow for onSelectStation — a fresh closure every
  // parent render. Route clicks through a ref (stable identity, .current updated
  // each render) so drawLayers doesn't list the prop as a dep; otherwise every
  // parent state change (detail open, announcement, toggle) would schedule a
  // full teardown+rebuild of ~36 polylines + up to ~100 markers.
  const onSelectStationRef = useRef(onSelectStation);
  onSelectStationRef.current = onSelectStation;

  const lens = darkMode
    ? { ...LENS.dark, ink: '#E9E7E2', muted: '#94897B' }
    : { ...LENS.light, ink: '#191512', muted: '#6B5B4F' };

  // ------ Initialize map (re-inits on theme change) ------
  useEffect(() => {
    if (!containerRef.current || !window.L) return;
    ensureMapStyles(darkMode);

    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const L = window.L;
    const isDesktop = window.innerWidth > 640;

    // If home is already known (re-entry / theme toggle), open centered on it so
    // the view doesn't snap back to the Bay-wide default (§2.7 continuity).
    const h = homeRef.current;
    const basemap = flowsBasemapMode();
    const m = L.map(containerRef.current, {
      center: h ? [h.lat, h.lng] : [37.76, -122.20],
      zoom: h ? HOME_ZOOM : 10,
      scrollWheelZoom: window.innerWidth > 768, // desktop-only gate (must-not-change)
      zoomControl: false,
      // P1: the tile layer used to own the zoom ceiling; the map owns it now
      // so both basemap modes behave identically.
      maxZoom: 19,
      // 'self' renders committed geography that ends at CONTEXT_BBOX — clamp
      // pan/zoom to the artifact so the frame never shows void past the data.
      ...(basemap === 'self'
        ? { maxBounds: CONTEXT_BBOX_LATLNG, maxBoundsViscosity: 1.0, minZoom: 8 }
        : {}),
    });
    // Desktop gets keyboard-operable +/- controls (§6.4 K3); mobile stays clean.
    if (isDesktop) L.control.zoom({ position: 'bottomright' }).addTo(m);

    if (basemap === 'carto') {
      const tileUrl = darkMode
        ? 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png'
        : 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png';
      // C1 (2026-07-16): the OSM/CARTO credit is LICENSE-REQUIRED (ODbL + CARTO
      // terms — lib/licensed-sources.js 'carto-osm') WHENEVER these tiles
      // render. attributionControl was suppressed here once; never suppress it
      // again — restyle it below instead. The guard
      // (tests/attribution-sources.test.js) fails the build if this string
      // leaves the bundle. P1 (2026-08-26): tiles + credit now live together
      // behind this branch — in 'self' mode NEITHER renders, because crediting
      // CARTO/OSM for geography they did not draw would be a false attribution
      // (tests/flows-basemap-self.test.js pins both directions).
      L.tileLayer(tileUrl, {
        maxZoom: 19,
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
      }).addTo(m);
    } else {
      // P1 'self' (default): zero third-party runtime dependency. The ground
      // pane holds committed Natural Earth land (public domain — no credit
      // owed, none rendered; see data/bay-context.geojson meta.license) drawn
      // by the effect below; water is the container background. BELOW the
      // tile pane (200) since 2026-08-28 so the detailed basemap paints over
      // it — still below the overlay pane, so transit lines can never sink
      // beneath the land fill, and it remains the visible floor whenever the
      // archive is unreachable.
      m.createPane(FLOWS_GROUND_PANE).style.zIndex = 150;
      // Detailed basemap (2026-08-28): our own Protomaps/OSM vector archive.
      // A rider reading a reachability band wants to know WHICH neighborhood
      // the band covers — street context answers that; a silhouette cannot.
      // Resolves null and changes nothing if the archive is unreachable.
      if (typeof window.attachDetailBasemap === 'function') {
        window.attachDetailBasemap(m, { darkMode }).then((layer) => {
          if (layer && mapRef.current !== m) {
            try { m.removeLayer(layer); } catch (_) { /* already gone */ }
          }
        });
      }
    }
    // Restyle Leaflet's own control to the lens: near-opaque scrim so the
    // credit clears WCAG AA regardless of the surface behind it. In 'self'
    // mode this carries only Leaflet's own prefix.
    const attrEl = m.attributionControl && m.attributionControl.getContainer();
    if (attrEl) {
      attrEl.style.cssText = darkMode
        ? 'background:rgba(12,15,15,0.92);color:#94897B;font:10px/1.7 var(--font-body,sans-serif);padding:0 6px;'
        : 'background:rgba(251,249,244,0.92);color:#6B5B4F;font:10px/1.7 var(--font-body,sans-serif);padding:0 6px;';
      attrEl.querySelectorAll('a').forEach((a) => {
        a.style.color = 'inherit';
        a.style.textDecoration = 'underline';
      });
    }

    // Map markers + tooltips are the non-accessible layer — the feed is the
    // contractual accessible surface (§6.3). Hide the panes from AT wholesale.
    const hidePane = (name) => { const p = m.getPane(name); if (p) p.setAttribute('aria-hidden', 'true'); };
    hidePane('markerPane'); hidePane('tooltipPane');

    // Redraw (re-cull labels) only when we cross the cull zoom boundary (§2.5).
    belowCullRef.current = m.getZoom() < LABEL_CULL_ZOOM;
    m.on('zoomend', () => {
      const below = m.getZoom() < LABEL_CULL_ZOOM;
      if (below !== belowCullRef.current) { belowCullRef.current = below; setZoomTick((t) => t + 1); }
    });

    mapRef.current = m;
    setMapReady((c) => c + 1);

    return () => {
      m.remove();
      mapRef.current = null;
      lineLayersRef.current = {};
      markerLayersRef.current = {};
    };
  }, [darkMode]);

  // ------ P1 self-basemap ground: land polygons over the water container ------
  // Re-runs when the map re-inits (mapReady bumps on theme change — the init
  // effect above is declared first, so the new map exists by the time this
  // fires) and when the artifact arrives. Null baseGeo (fetch failed) degrades
  // to bare water ground + transit shapes — page-never-dies.
  useEffect(() => {
    const m = mapRef.current;
    const L = window.L;
    if (!m || !L || !baseGeo) return;
    if (flowsBasemapMode() !== 'self') return;
    const G = darkMode ? LENS.dark : LENS.light;
    const ground = L.geoJSON(baseGeo, {
      pane: FLOWS_GROUND_PANE,
      interactive: false,
      style: {
        fillColor: G.groundLand, fillOpacity: 1,
        color: G.coast, weight: 1, opacity: 1, // hairline coastline
      },
    }).addTo(m);
    return () => { if (mapRef.current === m) m.removeLayer(ground); };
  }, [mapReady, baseGeo, darkMode]);

  // ------ Draw lines + reach markers + home ------
  const drawLayers = useCallback(() => {
    const m = mapRef.current;
    const L = window.L;
    // Lines are optional: if lines.json failed to load, still draw home + reach
    // markers on the bare tiles (graceful degrade). Only stations are required.
    if (!m || !L || !stations || stations.length === 0) return;

    Object.values(lineLayersRef.current).forEach((lg) => m.removeLayer(lg));
    Object.values(markerLayersRef.current).forEach((lg) => m.removeLayer(lg));
    lineLayersRef.current = {};
    markerLayersRef.current = {};

    const haloOpacity = darkMode ? 0.05 : 0.04;
    const coreOpacity = darkMode ? 0.50 : 0.60;
    const dotRing = hexA(lens.page, 0.9);

    // ── Lines: one quiet, glowing artery per feature, colored per SYSTEM ──
    const lineGroups = {};
    SYSTEMS.forEach((sys) => { lineGroups[sys] = L.layerGroup(); });
    const feats = (lines && lines.features) || [];
    feats.forEach((feature) => {
      const props = feature.properties || {};
      const sys = props.system;
      if (!lineGroups[sys]) return;
      const color = systemLineInk(sys, darkMode);
      const coreWeight = CORE_WEIGHT[sys] || 2;
      geojsonToLatLngs(feature.geometry).forEach((coords) => {
        if (!coords || coords.length < 2) return;
        const base = { color, lineCap: 'round', lineJoin: 'round', interactive: false };
        L.polyline(coords, Object.assign({}, base, {
          weight: 7, opacity: haloOpacity, className: 'flows-line-glow',
        })).addTo(lineGroups[sys]);
        L.polyline(coords, Object.assign({}, base, {
          weight: coreWeight, opacity: coreOpacity,
        })).addTo(lineGroups[sys]);
      });
    });
    lineLayersRef.current = lineGroups;

    // ── Reach markers: event-bearing stations, colored by time-from-home ──
    const markerGroups = {};
    SYSTEMS.forEach((sys) => { markerGroups[sys] = L.layerGroup(); });
    const reachMarkers = []; // { marker, band } for the entrance stagger

    if (home && distances && distances.size > 0) {
      const { nowMin, dayType } = pacificNow();
      const today = (window.FlowsFeedFormat && window.FlowsFeedFormat.pacificToday)
        ? window.FlowsFeedFormat.pacificToday() : null;

      // Candidate stations: TONIGHT-event-bearing, visible system, reachable
      // (§6.3 parity with the feed — see tonightCandidateStations header).
      let candidates = tonightCandidateStations(stations, eventMap, home, distances, visibleSystems, today);

      // Below the cull zoom, keep only the nearest-N by time (§2.5 declutter);
      // every marker that survives still carries its number.
      const below = m.getZoom() < LABEL_CULL_ZOOM;
      if (below && candidates.length > LABEL_CULL_KEEP) {
        candidates = candidates
          .slice()
          .sort((a, b) => distances.get(a.id).time - distances.get(b.id).time)
          .slice(0, LABEL_CULL_KEEP);
      }

      candidates.forEach((station) => {
        const d = distances.get(station.id);
        const band = window.bandForMinutes ? window.bandForMinutes(d.time) : null;
        const gone = lastTrainGone(station.system, nowMin, dayType, lastTrain);
        const fill = gone ? lens.dimFill : (reachColor(d.time, darkMode) || lens.reach4);
        const labelColor = gone ? lens.dimText : (reachColor(d.time, darkMode) || lens.reach4);
        // Glow is nearness: reach1 + reach2 only, and never when the train's gone.
        const wantGlow = !gone && (band === 'reach1' || band === 'reach2');
        const shadow = '0 0 0 2px ' + dotRing + (wantGlow ? ', 0 0 10px ' + hexA(fill, 0.55) : '');

        const icon = L.divIcon({
          className: '',
          html: '<span class="flows-reach-dot" style="width:10px;height:10px;background:'
            + fill + ';box-shadow:' + shadow + '"></span>',
          iconSize: [10, 10], iconAnchor: [5, 5],
        });
        const marker = L.marker([station.lat, station.lng], { icon, keyboard: false });
        marker.bindTooltip(
          '<span style="color:' + labelColor + '">' + minLabelText(d.time, gone) + '</span>',
          { permanent: true, direction: 'top', offset: [0, -10], className: 'flows-min-label' }
        );
        marker.on('click', () => {
          const onSelect = onSelectStationRef.current;
          if (onSelect) onSelect(station);
        });
        marker.addTo(markerGroups[station.system]);
        reachMarkers.push({ marker, band });
      });
    }
    markerLayersRef.current = markerGroups;

    // Add visible systems (lines + markers) to the map.
    SYSTEMS.forEach((sys) => {
      if (visibleSystems.has(sys)) {
        if (lineLayersRef.current[sys]) lineLayersRef.current[sys].addTo(m);
        if (markerLayersRef.current[sys]) markerLayersRef.current[sys].addTo(m);
      }
    });

    // ── Home "you are here" marker (§2.3) — above event markers, pulsing ──
    if (home) {
      const rings = reducedMotion ? ''
        : '<span class="flows-home-ring"></span><span class="flows-home-ring flows-home-ring-2"></span>';
      const homeIcon = L.divIcon({
        className: '',
        html: '<span class="flows-home-core"></span>' + rings,
        iconSize: [34, 34], iconAnchor: [17, 17],
      });
      const homeMarker = L.marker([home.lat, home.lng], {
        icon: homeIcon, keyboard: false, zIndexOffset: 1000,
      });
      const homeName = window.displayStationName ? window.displayStationName(home) : (home.name || '');
      homeMarker.bindTooltip('YOU — ' + homeName, {
        permanent: true, direction: 'top', offset: [0, -14], className: 'flows-home-label',
      });
      // Standalone layer (not in any system group) so a system toggle can never
      // hide your anchor. The toggle-visibility effect only iterates SYSTEMS.
      const homeGroup = L.layerGroup([homeMarker]);
      homeGroup.addTo(m);
      markerLayersRef.current.__home = homeGroup;
    }

    // Re-hide the panes (Leaflet re-creates children on redraw).
    const hidePane = (name) => { const p = m.getPane(name); if (p) p.setAttribute('aria-hidden', 'true'); };
    hidePane('markerPane'); hidePane('tooltipPane');

    // ── Framing + entrance (§2.7) ──
    const homeChanged = home && home.id !== lastHomeIdRef.current;
    if (home) {
      // Framing flies/snaps once per home change — immediately, for continuity.
      if (homeChanged) {
        lastHomeIdRef.current = home.id;
        if (reducedMotion) m.setView([home.lat, home.lng], HOME_ZOOM);
        else m.flyTo([home.lat, home.lng], HOME_ZOOM, { duration: 0.9 });
      }
      // B11: the staggered marker entrance keys to the FIRST draw that actually
      // produces markers for this home — distances arrive a draw (or two) after
      // the home is set on a split fetch, so keying the stagger to homeChanged
      // spent it on an empty draw and left the real markers to pop in un-staggered.
      // Now the fly fires on home-change; the band stagger waits for real markers.
      if (!reducedMotion && reachMarkers.length > 0 && home.id !== lastStaggerHomeIdRef.current) {
        lastStaggerHomeIdRef.current = home.id;
        // Fade markers in by band (reach1 first), 90ms between bands.
        reachMarkers.forEach(({ marker, band }) => {
          const el = marker.getElement();
          if (!el) return;
          el.style.transition = 'opacity 380ms ease-out';
          el.style.opacity = '0';
          const delay = (BAND_ORDER[band] || 0) * 90;
          window.setTimeout(() => { el.style.opacity = '1'; }, 40 + delay);
        });
      }
      // else: a redraw from a toggle/marker-tap — keep the current view + markers.
    } else {
      lastHomeIdRef.current = null;
      lastStaggerHomeIdRef.current = null; // a later re-set home re-staggers
      const networkStations = (stations || []).filter((s) => visibleSystems.has(s.system));
      if (networkStations.length > 1) {
        m.fitBounds(L.latLngBounds(networkStations.map((s) => [s.lat, s.lng])), {
          padding: [50, 40], maxZoom: 11,
        });
      }
    }
  }, [stations, eventMap, distances, home, visibleSystems, lines, lastTrain, darkMode, reducedMotion]);

  // Draw when data/map/zoom-band changes (small delay lets Leaflet size the
  // container). drawLayers is a useCallback that already recomputes when any of
  // its data deps change; mapReady + zoomTick are the effect-only triggers.
  useEffect(() => {
    if (!mapRef.current || !stations || stations.length === 0) return;
    const timer = setTimeout(() => { if (mapRef.current) drawLayers(); }, 200);
    return () => clearTimeout(timer);
  }, [drawLayers, mapReady, zoomTick, stations]);

  // Toggle layer visibility without a full redraw.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    SYSTEMS.forEach((sys) => {
      const lineLayer = lineLayersRef.current[sys];
      const markerLayer = markerLayersRef.current[sys];
      if (visibleSystems.has(sys)) {
        if (lineLayer && !m.hasLayer(lineLayer)) lineLayer.addTo(m);
        if (markerLayer && !m.hasLayer(markerLayer)) markerLayer.addTo(m);
      } else {
        if (lineLayer && m.hasLayer(lineLayer)) m.removeLayer(lineLayer);
        if (markerLayer && m.hasLayer(markerLayer)) m.removeLayer(markerLayer);
      }
    });
  }, [visibleSystems]);

  // --- Render ---
  return (
    React.createElement('div', null,
      // Layer toggles (4 — BART/CALTRAIN/MUNI/VTA)
      React.createElement('div', {
        style: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
      },
        SYSTEMS.map((sys) => {
          const info = (window.SYSTEM_INFO && window.SYSTEM_INFO[sys]) || { label: sys.toUpperCase(), color: '#888' };
          return React.createElement(TransitLayerToggle, {
            key: sys, system: sys, label: (info.label || sys).toUpperCase(), color: info.color,
            active: visibleSystems.has(sys), onToggle: onToggleSystem, lens, darkMode,
          });
        })
      ),

      // Map container + no-home hint
      React.createElement('div', { style: { position: 'relative' } },
        React.createElement('div', {
          ref: containerRef,
          className: 'focus-double-ring',
          style: {
            width: '100%',
            height: height || 520,
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid ' + lens.hairline,
            // P1 'self': the container IS the water — inline style outranks
            // Leaflet's default grey. Both themes intentional
            // (feedback_dark_light_mode). Carto mode keeps tiles as ground.
            ...(flowsBasemapMode() === 'self' ? { background: lens.groundWater } : {}),
          },
        }),
        // No home base set → quiet network + hint (§2.6). Hint is aria-hidden;
        // the feed's set-home card is the accessible path.
        !home && React.createElement('div', {
          'aria-hidden': 'true',
          style: {
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, pointerEvents: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', borderRadius: 100,
            whiteSpace: 'nowrap', maxWidth: 'calc(100% - 24px)',
            ...glassStyle(darkMode, 'solid'),
            border: '1px solid ' + lens.hairline,
            boxShadow: '0 10px 30px rgba(0,0,0,' + (darkMode ? '0.35' : '0.14') + ')',
          },
        },
          React.createElement('span', {
            style: {
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 800,
              letterSpacing: '0.08em', textTransform: 'uppercase', color: lens.homeAccent,
            },
          }, 'Set a home base'),
          React.createElement('span', {
            style: {
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500, color: lens.muted,
            },
          }, 'to light up what you can reach')
        )
      )
    )
  );
}

FlowsTransitMap.propTypes = {
  stations: PropTypes.array.isRequired,
  eventMap: PropTypes.object.isRequired,
  activeStation: PropTypes.object,
  homeStation: PropTypes.object,
  onSelectStation: PropTypes.func,
  visibleSystems: PropTypes.object.isRequired,
  onToggleSystem: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  distances: PropTypes.object,
  lines: PropTypes.object,
  lastTrain: PropTypes.object,
  landmask: PropTypes.object,
  baseGeo: PropTypes.object,
};

FlowsTransitMap.defaultProps = {
  activeStation: null,
  homeStation: null,
  onSelectStation: null,
  height: 520,
  distances: null,
  lines: null,
  lastTrain: null,
  landmask: null,
  baseGeo: null,
};

window.FlowsTransitMap = FlowsTransitMap;
